/**
 * HALO - Admin Dashboard
 * Employee management for organization admins
 * Design: Clean admin interface with stats and employee list
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  User,
  UserPlus,
  Eye,
  EyeOff,
  X,
  Check,
  AlertCircle,
  Copy,
  RefreshCw,
  Search,
  Building2,
  ChevronDown,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  getOrganizationEmployees,
  createEmployeeAccount,
  updateEmployee,
  deleteEmployee,
  getOrganization,
  getOrganizationDepartments,
  getDepartmentBreakdown,
  type UserRow,
} from "../lib/supabase";
import { SectionCards } from "@/components/dashboard/SectionCards";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AdminDashboardProps {
  organizationId: string;
  userId: string;
}

export default function AdminDashboard({
  organizationId,
  userId,
}: AdminDashboardProps) {
  const [employees, setEmployees] = useState<UserRow[]>([]);
  const [organization, setOrganization] = useState<any>(null);
  const [departments, setDepartments] = useState<string[]>([]);
  const [departmentBreakdown, setDepartmentBreakdown] = useState<
    Array<{ department: string; count: number }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<UserRow | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingEmployee, setDeletingEmployee] = useState<UserRow | null>(
    null,
  );
  const [createdCredentials, setCreatedCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Load data
  useEffect(() => {
    loadData();
  }, [organizationId]);

  const loadData = async () => {
    setLoading(true);
    const [emps, org, depts, breakdown] = await Promise.all([
      getOrganizationEmployees(),
      getOrganization(organizationId),
      getOrganizationDepartments(),
      getDepartmentBreakdown(),
    ]);
    setEmployees(emps);
    setOrganization(org);
    setDepartments(depts);
    setDepartmentBreakdown(breakdown);
    setLoading(false);
  };

  const handleCreateEmployee = async (data: {
    email: string;
    password: string;
    fullName: string;
    department: string;
    role: "employee" | "manager";
  }) => {
    const result = await createEmployeeAccount(
      data.email,
      data.password,
      data.fullName,
      data.department,
      data.role,
    );

    if (result.success) {
      setCreatedCredentials({ email: data.email, password: data.password });
      await loadData();
      return { success: true };
    }
    return { success: false, error: result.error };
  };

  const handleToggleActive = async (employee: UserRow) => {
    await updateEmployee(employee.id, { is_active: !employee.is_active });
    await loadData();
  };

  const handleUpdateRole = async (
    employee: UserRow,
    newRole: "employee" | "manager",
  ) => {
    await updateEmployee(employee.id, { role: newRole });
    await loadData();
  };

  const handleEditEmployee = (employee: UserRow) => {
    setEditingEmployee(employee);
    setShowEditModal(true);
  };

  const handleUpdateEmployee = async (data: {
    id: string;
    fullName: string;
    department: string;
    role: "employee" | "manager";
  }) => {
    const result = await updateEmployee(data.id, {
      full_name: data.fullName,
      department: data.department,
      role: data.role,
    });

    if (result.success) {
      await loadData();
      return { success: true };
    }
    return { success: false, error: result.error };
  };

  const handleDeleteClick = (employee: UserRow) => {
    setDeletingEmployee(employee);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingEmployee)
      return { success: false, error: "No employee selected" };

    const result = await deleteEmployee(deletingEmployee.id);

    if (result.success) {
      await loadData();
      return { success: true };
    }
    return { success: false, error: result.error };
  };

  // Filter employees
  const filteredEmployees = employees.filter(
    (e) =>
      e.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (e.department &&
        e.department.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  // Count stats
  const activeEmployees = employees.filter(
    (e) => e.is_active && e.id !== userId,
  );
  const totalEmployees = employees.filter((e) => e.id !== userId);

  const adminCards = [
    {
      title: "Total Employees",
      value: loading ? "—" : totalEmployees.length,
      description: loading
        ? "Loading employee data..."
        : `${totalEmployees.length} employees registered`,
      footer: "Excluding admin account",
      icon: <Users className="w-4 h-4 text-signal-blue" />,
    },
    {
      title: "Active Employees",
      value: loading ? "—" : activeEmployees.length,
      description: loading
        ? "Loading active status..."
        : `${activeEmployees.length} employees currently active`,
      footer: "Active in the system",
      icon: <Check className="w-4 h-4 text-signal-green" />,
    },
    {
      title: "Organization",
      value: loading ? "—" : organization?.name || "Not set",
      description: loading
        ? "Loading organization..."
        : organization?.name
          ? "Organization configured"
          : "Setup required",
      footer: "Organization settings",
      icon: <Building2 className="w-4 h-4 text-primary" />,
    },
  ];

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      {/* Section Cards */}
      <SectionCards cards={adminCards} />

      {/* Main Content with Sidebar */}
      <div className="max-w-9xl w-full mr-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Employee List */}
          <div className="lg:col-span-2">
            {/* Employee Management Section */}
            <div className="halo-card">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    Employee Accounts
                  </h2>
                  <p className="text-sm text-muted-slate">
                    Create and manage employee accounts for your organization
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <motion.button
                    onClick={loadData}
                    className="p-2.5 hover:bg-surface-elevated rounded-xl transition-colors text-muted-slate"
                    whileTap={{ scale: 0.95 }}
                    title="Refresh"
                  >
                    <RefreshCw
                      className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
                    />
                  </motion.button>
                  <motion.button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-signal-green hover:opacity-90 text-white rounded-xl font-medium transition-all duration-micro"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <UserPlus className="w-4 h-4" />
                    Add Employee
                  </motion.button>
                </div>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-slate" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="halo-input pl-11"
                />
              </div>

              {/* Employee List */}
              <div className="space-y-2">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-muted-slate">Loading employees...</p>
                  </div>
                ) : filteredEmployees.length === 0 ? (
                  <div className="text-center py-12 halo-card border-dashed">
                    <Users className="w-12 h-12 text-muted-slate mx-auto mb-4 opacity-30" />
                    <p className="text-muted-slate">No employees found</p>
                    <p className="text-sm text-muted-slate mt-1">
                      {searchQuery
                        ? "Try adjusting your search"
                        : 'Click "Add Employee" to create your first employee account'}
                    </p>
                  </div>
                ) : (
                  filteredEmployees.map((employee, index) => (
                    <EmployeeRow
                      key={employee.id}
                      employee={employee}
                      isCurrentUser={employee.id === userId}
                      onToggleActive={() => handleToggleActive(employee)}
                      onUpdateRole={(role) => handleUpdateRole(employee, role)}
                      onEdit={() => handleEditEmployee(employee)}
                      onDelete={() => handleDeleteClick(employee)}
                      index={index}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar - Department Breakdown */}
          <div className="lg:col-span-1">
            <div className="halo-card sticky top-6">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">
                  Departments
                </h3>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : departmentBreakdown.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-muted-slate mx-auto mb-3 opacity-30" />
                  <p className="text-sm text-muted-slate">No departments yet</p>
                  <p className="text-xs text-muted-slate/60 mt-1">
                    Create departments when adding employees
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {departmentBreakdown.map((dept, index) => (
                    <div
                      key={dept.department}
                      className="flex items-center justify-between p-3 rounded-lg bg-surface-dark border border-glass-grey hover:border-muted-slate transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                          {index + 1}
                        </div>
                        <span className="text-sm font-medium text-foreground">
                          {dept.department}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-muted-slate bg-surface-elevated px-2 py-1 rounded-md">
                        {dept.count}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-glass-grey">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-slate">Total Departments</span>
                  <span className="font-medium text-foreground">
                    {departmentBreakdown.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Employee Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateEmployeeModal
            onClose={() => {
              setShowCreateModal(false);
              setCreatedCredentials(null);
            }}
            onCreate={handleCreateEmployee}
            createdCredentials={createdCredentials}
            existingDepartments={departments}
          />
        )}
      </AnimatePresence>

      {/* Edit Employee Modal */}
      <AnimatePresence>
        {showEditModal && editingEmployee && (
          <EditEmployeeModal
            employee={editingEmployee}
            onClose={() => {
              setShowEditModal(false);
              setEditingEmployee(null);
            }}
            onUpdate={handleUpdateEmployee}
            existingDepartments={departments}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Dialog */}
      <AnimatePresence>
        {showDeleteConfirm && deletingEmployee && (
          <DeleteConfirmDialog
            employee={deletingEmployee}
            onClose={() => {
              setShowDeleteConfirm(false);
              setDeletingEmployee(null);
            }}
            onConfirm={handleConfirmDelete}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================================================
// EMPLOYEE ROW COMPONENT
// ============================================================================

interface EmployeeRowProps {
  employee: UserRow;
  isCurrentUser: boolean;
  onToggleActive: () => void;
  onUpdateRole: (role: "employee" | "manager") => void;
  onEdit: () => void;
  onDelete: () => void;
  index: number;
}

function EmployeeRow({
  employee,
  isCurrentUser,
  onToggleActive,
  onUpdateRole,
  onEdit,
  onDelete,
  index,
}: EmployeeRowProps) {
  return (
    <motion.div
      className="flex items-center justify-between p-4 rounded-xl bg-surface-dark border border-glass-grey hover:border-muted-slate transition-all duration-micro"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <div className="flex items-center gap-4">
        {/* Avatar */}
        <div className="relative">
          {employee.avatar_url ? (
            <img
              src={employee.avatar_url}
              alt={employee.full_name}
              className="w-10 h-10 rounded-xl object-cover ring-1 ring-border"
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-surface-elevated flex items-center justify-center text-muted-slate border border-glass-grey">
              <User className="w-5 h-5" />
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-foreground">{employee.full_name}</p>
            {isCurrentUser && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                You
              </span>
            )}
          </div>
          <p className="text-sm text-muted-slate">{employee.email}</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Department */}
        {employee.department && (
          <span className="text-sm text-muted-slate hidden sm:block">
            {employee.department}
          </span>
        )}

        {/* Role Badge */}
        {/* <span
          className={`text-xs px-3 py-1 rounded-full border ${
            roleColors[employee.role]
          }`}
        >
          {employee.role}
        </span> */}

        {/* Status */}
        <span
          className={`text-xs px-3 py-1 rounded-full ${
            employee.is_active
              ? "bg-signal-green/10 text-signal-green"
              : "bg-surface-elevated text-muted-slate"
          }`}
        >
          {employee.is_active ? "Active" : "Inactive"}
        </span>

        {/* Actions */}
        {!isCurrentUser && employee.role !== "admin" && (
          <div className="flex items-center gap-2">
            <select
              value={employee.role}
              onChange={(e) =>
                onUpdateRole(e.target.value as "employee" | "manager")
              }
              className="text-sm bg-surface-dark border border-glass-grey rounded-lg px-2 py-1.5 text-foreground focus:border-primary focus:outline-none"
            >
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
            </select>
            <button
              onClick={onToggleActive}
              className="p-2 hover:bg-surface-elevated rounded-lg transition-colors text-muted-slate hover:text-foreground"
              title={employee.is_active ? "Deactivate" : "Activate"}
            >
              {employee.is_active ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={onEdit}
              className="p-2 hover:bg-surface-elevated rounded-lg transition-colors text-muted-slate hover:text-foreground"
              title="Edit employee"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 hover:bg-surface-elevated rounded-lg transition-colors text-muted-slate hover:text-signal-crimson"
              title="Delete employee"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================================================
// CREATE EMPLOYEE MODAL
// ============================================================================

interface CreateEmployeeModalProps {
  onClose: () => void;
  onCreate: (data: {
    email: string;
    password: string;
    fullName: string;
    department: string;
    role: "employee" | "manager";
  }) => Promise<{ success: boolean; error?: string }>;
  createdCredentials: { email: string; password: string } | null;
  existingDepartments: string[];
}

function CreateEmployeeModal({
  onClose,
  onCreate,
  createdCredentials,
  existingDepartments,
}: CreateEmployeeModalProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [department, setDepartment] = useState("");
  const [isCreatingNewDept, setIsCreatingNewDept] = useState(false);
  const [role, setRole] = useState<"employee" | "manager">("employee");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  // Generate random password
  const generatePassword = () => {
    const chars =
      "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%";
    let pass = "";
    for (let i = 0; i < 12; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(pass);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await onCreate({
      email,
      password,
      fullName,
      department,
      role,
    });

    setLoading(false);
    if (!result.success) {
      setError(result.error || "Failed to create employee");
    }
  };

  const copyCredentials = () => {
    if (createdCredentials) {
      const text = `Email: ${createdCredentials.email}\nPassword: ${createdCredentials.password}`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Show success screen after creation
  if (createdCredentials) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="w-full max-w-md shadow-2xl">
            <CardContent className="pt-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-signal-green/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-signal-green" />
                </div>
                <CardTitle className="text-2xl font-semibold text-foreground">
                  Employee Created!
                </CardTitle>
                <CardDescription className="mt-2">
                  Share these credentials with the employee
                </CardDescription>
              </div>

              <div className="bg-surface-elevated rounded-xl p-4 mb-6 border border-glass-grey">
                <div className="mb-3">
                  <label className="text-xs text-muted-slate uppercase tracking-wider">
                    Email
                  </label>
                  <p className="text-foreground font-mono text-sm mt-1">
                    {createdCredentials.email}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-muted-slate uppercase tracking-wider">
                    Password
                  </label>
                  <p className="text-foreground font-mono text-sm mt-1">
                    {createdCredentials.password}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <motion.button
                  onClick={copyCredentials}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-surface-elevated hover:bg-glass-grey text-foreground rounded-xl transition-colors border border-glass-grey"
                  whileTap={{ scale: 0.98 }}
                >
                  <Copy className="w-4 h-4" />
                  {copied ? "Copied!" : "Copy Credentials"}
                </motion.button>
                <motion.button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 bg-signal-green hover:opacity-90 text-white rounded-xl font-medium transition-opacity"
                  whileTap={{ scale: 0.98 }}
                >
                  Done
                </motion.button>
              </div>

              <p className="text-xs text-center text-muted-slate mt-4">
                The employee can now sign in to the desktop app with these
                credentials.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <Card className="w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
          <CardHeader className="flex-row items-center justify-between pb-4">
            <CardTitle className="text-xl font-semibold text-foreground">
              Add Employee
            </CardTitle>
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-elevated rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-muted-slate" />
            </button>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-xl bg-signal-crimson/10 border border-signal-crimson/20 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-signal-crimson flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-signal-crimson">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-muted-slate mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="halo-input"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-slate mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="halo-input"
                  placeholder="employee@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-slate mb-2">
                  Password *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="halo-input font-mono flex-1"
                    placeholder="Min 6 characters"
                  />
                  <motion.button
                    type="button"
                    onClick={generatePassword}
                    className="px-4 py-3 bg-surface-elevated hover:bg-glass-grey text-foreground rounded-xl transition-colors border border-glass-grey text-sm whitespace-nowrap"
                    whileTap={{ scale: 0.98 }}
                  >
                    Generate
                  </motion.button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-slate mb-2">
                    Department
                  </label>
                  {isCreatingNewDept ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                        className="halo-input"
                        placeholder="Enter new department name"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreatingNewDept(false);
                          setDepartment("");
                        }}
                        className="text-xs text-muted-slate hover:text-foreground transition-colors"
                      >
                        ← Back to department list
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="halo-input flex items-center justify-between w-full text-left"
                          >
                            <span
                              className={
                                department
                                  ? "text-foreground"
                                  : "text-muted-slate"
                              }
                            >
                              {department || "Select a department"}
                            </span>
                            <ChevronDown className="w-4 h-4 text-muted-slate" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                          {existingDepartments.length > 0 && (
                            <>
                              {existingDepartments.map((dept) => (
                                <DropdownMenuItem
                                  key={dept}
                                  onClick={() => setDepartment(dept)}
                                  className={
                                    department === dept
                                      ? "bg-surface-elevated"
                                      : ""
                                  }
                                >
                                  {dept}
                                </DropdownMenuItem>
                              ))}
                            </>
                          )}
                          <DropdownMenuItem
                            onClick={() => {
                              setIsCreatingNewDept(true);
                              setDepartment("");
                            }}
                            className="text-primary"
                          >
                            + Create new department
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-slate mb-2">
                    Role
                  </label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="halo-input flex items-center justify-between w-full text-left"
                      >
                        <span className="capitalize text-foreground">
                          {role}
                        </span>
                        <ChevronDown className="w-4 h-4 text-muted-slate" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                      <DropdownMenuItem
                        onClick={() => setRole("employee")}
                        className={
                          role === "employee" ? "bg-surface-elevated" : ""
                        }
                      >
                        Employee
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setRole("manager")}
                        className={
                          role === "manager" ? "bg-surface-elevated" : ""
                        }
                      >
                        Manager
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={loading}
                className="w-full halo-btn halo-btn--primary mt-6"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Creating...
                  </span>
                ) : (
                  "Create Employee Account"
                )}
              </motion.button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// ============================================================================
// EDIT EMPLOYEE MODAL
// ============================================================================

interface EditEmployeeModalProps {
  employee: UserRow;
  onClose: () => void;
  onUpdate: (data: {
    id: string;
    fullName: string;
    department: string;
    role: "employee" | "manager";
  }) => Promise<{ success: boolean; error?: string }>;
  existingDepartments: string[];
}

function EditEmployeeModal({
  employee,
  onClose,
  onUpdate,
  existingDepartments,
}: EditEmployeeModalProps) {
  const [fullName, setFullName] = useState(employee.full_name);
  const [department, setDepartment] = useState(employee.department || "");
  const [isCreatingNewDept, setIsCreatingNewDept] = useState(false);
  const [role, setRole] = useState<"employee" | "manager">(
    employee.role as "employee" | "manager",
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await onUpdate({
      id: employee.id,
      fullName,
      department,
      role,
    });

    setLoading(false);
    if (result.success) {
      onClose();
    } else {
      setError(result.error || "Failed to update employee");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <Card className="w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
          <CardHeader className="flex-row items-center justify-between pb-4">
            <CardTitle className="text-xl font-semibold text-foreground">
              Edit Employee
            </CardTitle>
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-elevated rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-muted-slate" />
            </button>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-xl bg-signal-crimson/10 border border-signal-crimson/20 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-signal-crimson flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-signal-crimson">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-muted-slate mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="halo-input"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-slate mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={employee.email}
                  disabled
                  className="halo-input bg-surface-elevated text-muted-slate cursor-not-allowed"
                />
                <p className="text-xs text-muted-slate mt-1">
                  Email cannot be changed
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-slate mb-2">
                    Department
                  </label>
                  {isCreatingNewDept ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                        className="halo-input"
                        placeholder="Enter new department name"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreatingNewDept(false);
                          setDepartment(employee.department || "");
                        }}
                        className="text-xs text-muted-slate hover:text-foreground transition-colors"
                      >
                        ← Back to department list
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="halo-input flex items-center justify-between w-full text-left"
                          >
                            <span
                              className={
                                department
                                  ? "text-foreground"
                                  : "text-muted-slate"
                              }
                            >
                              {department || "Select a department"}
                            </span>
                            <ChevronDown className="w-4 h-4 text-muted-slate" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                          {existingDepartments.length > 0 && (
                            <>
                              {existingDepartments.map((dept) => (
                                <DropdownMenuItem
                                  key={dept}
                                  onClick={() => setDepartment(dept)}
                                  className={
                                    department === dept
                                      ? "bg-surface-elevated"
                                      : ""
                                  }
                                >
                                  {dept}
                                </DropdownMenuItem>
                              ))}
                            </>
                          )}
                          <DropdownMenuItem
                            onClick={() => {
                              setIsCreatingNewDept(true);
                              setDepartment("");
                            }}
                            className="text-primary"
                          >
                            + Create new department
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-slate mb-2">
                    Role
                  </label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="halo-input flex items-center justify-between w-full text-left"
                      >
                        <span className="capitalize text-foreground">
                          {role}
                        </span>
                        <ChevronDown className="w-4 h-4 text-muted-slate" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                      <DropdownMenuItem
                        onClick={() => setRole("employee")}
                        className={
                          role === "employee" ? "bg-surface-elevated" : ""
                        }
                      >
                        Employee
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setRole("manager")}
                        className={
                          role === "manager" ? "bg-surface-elevated" : ""
                        }
                      >
                        Manager
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={loading}
                className="w-full halo-btn halo-btn--primary mt-6"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  "Save Changes"
                )}
              </motion.button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// ============================================================================
// DELETE CONFIRMATION DIALOG
// ============================================================================

interface DeleteConfirmDialogProps {
  employee: UserRow;
  onClose: () => void;
  onConfirm: () => Promise<{ success: boolean; error?: string }>;
}

function DeleteConfirmDialog({
  employee,
  onClose,
  onConfirm,
}: DeleteConfirmDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    setError("");
    setLoading(true);

    const result = await onConfirm();

    setLoading(false);
    if (result.success) {
      onClose();
    } else {
      setError(result.error || "Failed to delete employee");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <Card className="w-full max-w-md shadow-2xl">
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-signal-crimson/10 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-signal-crimson" />
              </div>
              <CardTitle className="text-2xl font-semibold text-foreground">
                Delete Employee?
              </CardTitle>
              <CardDescription className="mt-2">
                This action cannot be undone. This will permanently delete{" "}
                <span className="text-foreground font-medium">
                  {employee.full_name}
                </span>
                &apos;s account and all associated data.
              </CardDescription>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-signal-crimson/10 border border-signal-crimson/20 flex items-start gap-3 mb-4">
                <AlertCircle className="w-5 h-5 text-signal-crimson flex-shrink-0 mt-0.5" />
                <p className="text-sm text-signal-crimson">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <motion.button
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-surface-elevated hover:bg-glass-grey text-foreground rounded-xl transition-colors border border-glass-grey"
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </motion.button>
              <motion.button
                onClick={handleConfirm}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-signal-crimson hover:bg-signal-crimson/90 text-white rounded-xl font-medium transition-colors"
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Deleting...
                  </span>
                ) : (
                  "Delete"
                )}
              </motion.button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
